import { Box, TextField, FormControlLabel, Checkbox, Typography, Divider, FormHelperText } from '@mui/material';
import type { SubjectRules } from '../RulesConfigurationDialog';

export interface SubjectRulesTabProps {
  rules: SubjectRules;
  onChange: (rules: SubjectRules) => void;
}

export function SubjectRulesTab({ rules, onChange }: SubjectRulesTabProps) {
  const handleChange = (field: keyof SubjectRules, value: any) => {
    onChange({ ...rules, [field]: value });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Period Distribution */}
      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Period Distribution
        </Typography>
        <Typography variant="caption" color="text.secondary" paragraph>
          Control how subject periods are spread across the week during auto-fill.
        </Typography>

        <Box sx={{ mt: 2 }}>
          <TextField
            label="Max Periods Per Day Per Class"
            type="number"
            value={rules.maxPeriodsPerDayPerClass}
            onChange={(e) => handleChange('maxPeriodsPerDayPerClass', parseInt(e.target.value) || 0)}
            inputProps={{ min: 1, max: 8 }}
            sx={{ width: '50%', pr: 1 }}
            helperText="Auto-fill will not assign the same subject more than this many times in one day for a class"
          />

          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rules.distributeEvenly}
                  onChange={(e) => handleChange('distributeEvenly', e.target.checked)}
                />
              }
              label="Distribute Evenly Across Week"
            />
            <FormHelperText sx={{ ml: 4, mt: 0 }}>
              When enabled, auto-fill prefers subjects not yet assigned on the current day,
              spreading them across the week rather than clustering on the same days
            </FormHelperText>
          </Box>
        </Box>
      </Box>

      <Divider />

      <Box
        sx={{
          p: 2,
          backgroundColor: 'info.light',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'info.main',
        }}
      >
        <Typography variant="caption" color="info.dark">
          <strong>Note:</strong> Max Periods Per Day Per Class is a hard filter in auto-fill —
          once a subject reaches the daily limit for a section, it is skipped for remaining
          slots that day. Distribute Evenly is a soft preference that influences slot ordering.
        </Typography>
      </Box>
    </Box>
  );
}
